import React, { useRef, useState } from 'react';
import { X } from 'lucide-react';

/**
 * A list of options as tags: type one and press Enter (or a comma, or leave
 * the field) and it becomes a tag; the × on a tag removes it; Backspace in
 * an empty field removes the last one. A pasted "yes, no, unsure" becomes
 * three tags. Blanks and repeats are dropped.
 *
 * The same control wherever options are entered — a category output, a
 * grader's options, an output field's members — so a list of options never
 * has to be read as comma-separated text.
 */
export default function TagInput({
  value = [], onChange, placeholder = 'Add an option', ariaLabel = 'Options', readOnly = false, min = 0,
}) {
  const [text, setText] = useState('');
  const inputRef = useRef(null);
  const add = (raw) => {
    const parts = String(raw || '').split(',').map((s) => s.trim()).filter(Boolean);
    const next = [...value];
    for (const p of parts) if (!next.includes(p)) next.push(p);
    if (next.length !== value.length) onChange(next);
    setText('');
  };
  const remove = (i) => { if (value.length > min) onChange(value.filter((_, j) => j !== i)); };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div role="group" aria-label={ariaLabel} onClick={() => inputRef.current?.focus()}
      className={`flex min-h-[32px] w-full flex-wrap items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-1.5 py-1 focus-within:border-[color:var(--border-strong)] ${readOnly ? 'opacity-80' : 'cursor-text'}`}>
      {value.map((v, i) => (
        <span key={v} data-testid="tag" className="inline-flex h-6 max-w-full items-center gap-1 rounded bg-[color:var(--surface-sunken)] pl-2 pr-0.5 font-mono text-[12px] text-[color:var(--text-primary)]">
          <span className="truncate">{v}</span>
          {!readOnly && value.length > min && (
            <button type="button" aria-label={`Remove ${v}`} onClick={(e) => { e.stopPropagation(); remove(i); }}
              className="rounded p-0.5 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-danger">
              <X size={11} aria-hidden />
            </button>
          )}
        </span>
      ))}
      {!readOnly && (
        <input ref={inputRef} value={text} aria-label={`Add to ${ariaLabel.toLowerCase()}`} placeholder={value.length ? '' : placeholder}
          onChange={(e) => { if (e.target.value.includes(',')) add(e.target.value); else setText(e.target.value); }}
          onBlur={() => { if (text.trim()) add(text); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add(text); }
            else if (e.key === 'Backspace' && !text && value.length) { e.preventDefault(); remove(value.length - 1); }
          }}
          className="h-6 min-w-[96px] flex-1 bg-transparent px-1 font-mono text-[12px] text-[color:var(--text-primary)] outline-none placeholder:font-sans placeholder:text-[color:var(--text-muted)]" />
      )}
    </div>
  );
}
